import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtMoney, ROLE_LABELS, MILESTONES } from "@/lib/constants";
import { RiskDot } from "@/components/RiskDot";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { CurrencyEur, Warning, ClockCountdown, Printer, DownloadSimple, ShieldWarning, PaperPlaneTilt, Gear } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

function KPI({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
        <Icon size={20} weight="bold" className={accent} />
      </div>
      <div className="mt-3 font-head text-2xl font-extrabold tracking-tight tabular-nums">{value}</div>
    </div>
  );
}

export default function Exec() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [cfg, setCfg] = useState(false);
  const [recipients, setRecipients] = useState("");
  const [sending, setSending] = useState(false);
  const [esc, setEsc] = useState(null);
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState(false);
  const [risk, setRisk] = useState(null);
  const [riskDept, setRiskDept] = useState("all");

  useEffect(() => { api.get("/exec/summary").then((r) => setD(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/escalations/pending").then((r) => setEsc(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/escalations/history?limit=30").then((r) => setHistory(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (hasRole("admin", "pm")) api.get("/exec/risk-board").then((r) => setRisk(r.data)).catch(() => {}); }, [hasRole]);

  const runEscalations = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/escalations/run");
      const total = (data.advance_reminders || 0) + (data.advance_escalations || 0) +
        (data.block_reminders || 0) + (data.block_escalations || 0) +
        (data.task_reminders || 0) + (data.task_escalations || 0);
      toast.success(total ? `Escaladări procesate: ${total} notificare(i)` : "Nimic de escaladat momentan");
      const r = await api.get("/escalations/pending"); setEsc(r.data);
      const h = await api.get("/escalations/history?limit=30"); setHistory(h.data);
    } catch { toast.error("Verificare eșuată"); }
    finally { setRunning(false); }
  };

  const openCfg = async () => {
    try { const { data } = await api.get("/exec/report-recipients"); setRecipients((data.recipients || []).join(", ")); }
    catch { setRecipients(""); }
    setCfg(true);
  };
  const saveCfg = async () => {
    const list = recipients.split(",").map((e) => e.trim()).filter(Boolean);
    try { await api.put("/exec/report-recipients", { recipients: list }); toast.success("Destinatari salvați"); setCfg(false); }
    catch { toast.error("Eroare la salvare"); }
  };
  const sendNow = async () => {
    setSending(true);
    try { const { data } = await api.post("/exec/send-report"); toast.success(`Raport trimis către ${data.sent} destinatar(i)`); }
    catch { toast.error("Trimitere eșuată"); }
    finally { setSending(false); }
  };

  if (!d) return <div className="p-8 text-muted-foreground">Se încarcă…</div>;

  const exportCSV = () => {
    const head = "Cod,Proiect,Etapă,Risc,Marja%,TaskuriIntarziate,Buget,Costuri";
    const rows = d.projects_at_risk.map((p) => [p.code, `"${p.name}"`, p.milestone, p.risk_level, p.margin_pct, p.overdue_tasks, p.budget, p.costs].join(","));
    const csv = [head, ...rows].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "raport-conducere.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const barColor = { verde: "#10B981", galben: "#F59E0B", rosu: "#EF4444" };

  const fOverdue = risk ? risk.overdue.filter((o) => riskDept === "all" || o.department === riskDept) : [];
  const fRisky = risk ? risk.risky.filter((p) => riskDept === "all" || p.department === riskDept) : [];
  const exportRisk = () => {
    const head = "Tip,Departament,Cod proiect,Activitate/Proiect,Termen,Recomandat";
    const rows = [
      ...fOverdue.map((o) => ["Intarziere", o.department || "", o.project_code || "", `"${(o.activity || "").replace(/"/g, "'")}"`, o.due || "", ""].join(",")),
      ...fRisky.map((p) => ["Termen nerealist", p.department || "", p.code || "", `"${(p.name || "").replace(/"/g, "'")}"`, p.deadline || "", p.recommended || ""].join(",")),
    ];
    const csv = [head, ...rows].join("\n");
    const url = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a"); a.href = url; a.download = "panou-directori.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 fade-up" data-testid="exec-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Conducere</p>
          <h1 className="font-head text-3xl font-extrabold tracking-tighter">Rapoarte executive</h1>
        </div>
        <div className="flex gap-2 no-print">
          {hasRole("admin", "pm") && <Button variant="outline" onClick={runEscalations} disabled={running} data-testid="run-escalations-btn"><ShieldWarning size={16} className="mr-1.5" /> {running ? "Se verifică…" : "Verifică escaladări"}</Button>}
          {hasRole("admin", "pm") && <Button variant="outline" onClick={sendNow} disabled={sending} data-testid="send-report-btn"><PaperPlaneTilt size={16} className="mr-1.5" /> {sending ? "Se trimite…" : "Trimite raport"}</Button>}
          {hasRole("admin") && <Button variant="outline" onClick={openCfg} data-testid="report-cfg-btn"><Gear size={16} className="mr-1.5" /> Destinatari</Button>}
          <Button variant="outline" onClick={exportCSV} data-testid="export-csv-btn"><DownloadSimple size={16} className="mr-1.5" /> CSV</Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="exec-print-btn"><Printer size={16} className="mr-1.5" /> PDF</Button>
        </div>
      </div>

      <Dialog open={cfg} onOpenChange={setCfg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Destinatari raport săptămânal (Luni 08:00)</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Emailuri separate prin virgulă. Implicit: Directorul Executiv.</p>
          <Input value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="director@highberg.ro, ..." data-testid="report-recipients-input" />
          <DialogFooter><Button onClick={saveCfg} data-testid="report-recipients-save">Salvează</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="printable space-y-6">
        {risk && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/[0.06] p-5" data-testid="director-risk-board">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <ShieldWarning size={18} weight="bold" className="text-red-400" />
              <h3 className="font-head text-lg font-bold">Panou Directori — Întârzieri & Termene nerealiste</h3>
              {risk.recommended_from_now && (
                <span className="ml-1 text-xs text-muted-foreground">termen realist de azi: <span className="font-bold text-foreground">{risk.recommended_from_now}</span></span>
              )}
              <div className="ml-auto flex items-center gap-2 no-print">
                <Select value={riskDept} onValueChange={setRiskDept}>
                  <SelectTrigger className="h-8 w-[190px] text-xs" data-testid="risk-dept-filter"><SelectValue placeholder="Departament" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toate departamentele</SelectItem>
                    {(risk.departments || []).map((dep) => <SelectItem key={dep} value={dep}>{dep}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={exportRisk} data-testid="risk-export-csv"><DownloadSimple size={14} className="mr-1.5" /> CSV</Button>
                <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="risk-export-pdf"><Printer size={14} className="mr-1.5" /> PDF</Button>
              </div>
            </div>
            <div className="grid gap-5 md:grid-cols-2">
              <div>
                <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  <ClockCountdown size={14} weight="bold" className="text-amber-400" /> Activități întârziate ({fOverdue.length})
                </div>
                <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                  {fOverdue.map((o, i) => (
                    <div key={i} className="flex items-center justify-between rounded-md border border-border bg-card px-2.5 py-1.5 text-xs" data-testid={`risk-overdue-${i}`}>
                      <span className="min-w-0 flex-1 truncate"><span className="font-mono text-muted-foreground">{o.project_code}</span> · {o.activity}{o.department ? <span className="ml-1 text-muted-foreground">({o.department})</span> : null}</span>
                      <span className="ml-2 shrink-0 rounded bg-amber-500/15 px-1.5 py-0.5 font-bold text-amber-400 tabular-nums">{o.due}</span>
                    </div>
                  ))}
                  {fOverdue.length === 0 && <p className="text-xs text-muted-foreground">Nicio activitate întârziată. 🎉</p>}
                </div>
              </div>
              <div>
                <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  <Warning size={14} weight="bold" className="text-red-400" /> Proiecte cu termen nerealist ({fRisky.length})
                </div>
                <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                  {fRisky.map((p, i) => (
                    <div key={i} className="rounded-md border border-red-500/25 bg-card px-2.5 py-1.5 text-xs" data-testid={`risk-risky-${i}`}>
                      <div className="flex items-center justify-between">
                        <span className="min-w-0 flex-1 truncate font-semibold"><span className="font-mono text-muted-foreground">{p.code}</span> · {p.name}{p.department ? <span className="ml-1 font-normal text-muted-foreground">({p.department})</span> : null}</span>
                      </div>
                      <div className="mt-0.5 flex items-center gap-3 text-[11px] text-muted-foreground">
                        <span>Termen actual: <span className="font-bold text-red-400">{p.deadline}</span></span>
                        <span>Recomandat: <span className="font-bold text-emerald-400">{p.recommended}</span></span>
                      </div>
                    </div>
                  ))}
                  {fRisky.length === 0 && <p className="text-xs text-muted-foreground">Toate proiectele au termene realiste. 🎉</p>}
                </div>
              </div>
            </div>
          </div>
        )}
        {esc && (esc.advance_pending.length > 0 || esc.blocked_projects.length > 0 || esc.overdue_tasks.length > 0) ? (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-5 no-print" data-testid="escalations-panel">
            <div className="mb-3 flex items-center gap-2">
              <ShieldWarning size={18} weight="bold" className="text-amber-400" />
              <h3 className="font-head text-lg font-bold">Escaladări active</h3>
              <span className="ml-1 text-xs text-muted-foreground">reminder la {esc.remind_hours}h · Director la {esc.escalate_hours}h</span>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">Avans neîncasat ({esc.advance_pending.length})</div>
                <div className="space-y-1">
                  {esc.advance_pending.slice(0, 6).map((p) => (
                    <div key={p.id} onClick={() => navigate(`/projects/${p.id}`)} className="flex cursor-pointer items-center justify-between rounded-md border border-border bg-card px-2.5 py-1.5 text-xs hover:bg-white/[0.04]" data-testid={`esc-advance-${p.id}`}>
                      <span className="truncate">{p.code || p.name}</span>
                      <span className={cn("ml-2 shrink-0 rounded px-1.5 py-0.5 font-bold", p.hours >= esc.escalate_hours ? "bg-red-500/15 text-red-400" : p.hours >= esc.remind_hours ? "bg-amber-500/15 text-amber-400" : "text-muted-foreground")}>{Math.round(p.hours)}h</span>
                    </div>
                  ))}
                  {esc.advance_pending.length === 0 && <p className="text-xs text-muted-foreground">Toate avansurile confirmate.</p>}
                </div>
              </div>
              <div>
                <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">Proiecte blocate ({esc.blocked_projects.length})</div>
                <div className="space-y-1">
                  {esc.blocked_projects.slice(0, 6).map((p) => (
                    <div key={p.id} onClick={() => navigate(`/projects/${p.id}`)} className="flex cursor-pointer items-center justify-between rounded-md border border-border bg-card px-2.5 py-1.5 text-xs hover:bg-white/[0.04]" data-testid={`esc-block-${p.id}`}>
                      <span className="truncate">{p.code || p.name}</span>
                      <span className={cn("ml-2 shrink-0 rounded px-1.5 py-0.5 font-bold", p.hours >= esc.escalate_hours ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400")}>{Math.round(p.hours)}h</span>
                    </div>
                  ))}
                  {esc.blocked_projects.length === 0 && <p className="text-xs text-muted-foreground">Niciun proiect blocat.</p>}
                </div>
              </div>
              <div>
                <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">Task-uri depășite ({esc.overdue_tasks.length})</div>
                <div className="space-y-1">
                  {esc.overdue_tasks.slice(0, 6).map((t) => (
                    <div key={t.id} onClick={() => navigate(`/projects/${t.project_id}`)} className="flex cursor-pointer items-center justify-between rounded-md border border-border bg-card px-2.5 py-1.5 text-xs hover:bg-white/[0.04]" data-testid={`esc-task-${t.id}`}>
                      <span className="truncate">{t.title}</span>
                      <span className={cn("ml-2 shrink-0 rounded px-1.5 py-0.5 font-bold", t.hours >= esc.escalate_hours ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400")}>{Math.round(t.hours)}h</span>
                    </div>
                  ))}
                  {esc.overdue_tasks.length === 0 && <p className="text-xs text-muted-foreground">Niciun task depășit.</p>}
                </div>
              </div>
            </div>
          </div>
        ) : esc ? (
          <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.05] p-4 no-print flex items-center gap-2" data-testid="escalations-panel-empty">
            <ShieldWarning size={18} weight="bold" className="text-emerald-400" />
            <span className="text-sm text-muted-foreground">Nicio escaladare activă — toate avansurile confirmate, fără blocaje sau task-uri depășite.</span>
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KPI icon={CurrencyEur} label={`Marjă totală (${d.total_margin_pct}%)`} value={fmtMoney(d.total_margin)} accent="text-emerald-400" />
          <KPI icon={ClockCountdown} label="Task-uri întârziate" value={d.overdue_tasks} accent={d.overdue_tasks ? "text-red-400" : "text-muted-foreground"} />
          <KPI icon={ShieldWarning} label="Proiecte risc roșu" value={d.risk_counts.rosu} accent="text-red-400" />
          <KPI icon={Warning} label="Proiecte la risc" value={d.projects_at_risk.length} accent="text-amber-400" />
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-xl border border-border bg-card p-6 lg:col-span-2">
            <h3 className="mb-4 font-head text-lg font-bold">Distribuție pe etape workflow</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={d.milestone_distribution}>
                <XAxis dataKey="gate" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={{ background: "#111827", border: "1px solid #1f2937", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]} fill="#3B82F6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="mb-4 font-head text-lg font-bold">Risc & Încărcare pe rol</h3>
            <div className="space-y-3">
              {["verde", "galben", "rosu"].map((l) => (
                <div key={l} className="flex items-center justify-between"><RiskDot level={l} label size="lg" /><span className="font-head text-xl font-bold">{d.risk_counts[l] || 0}</span></div>
              ))}
            </div>
            <div className="mt-4 border-t border-border pt-3">
              <div className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Task-uri deschise / rol</div>
              {d.role_load.map((r) => (
                <div key={r.role} className="flex items-center justify-between py-0.5 text-sm"><span>{ROLE_LABELS[r.role] || r.role}</span><span className="font-semibold tabular-nums">{r.count}</span></div>
              ))}
              {d.role_load.length === 0 && <p className="text-sm text-muted-foreground">Niciun task deschis.</p>}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="mb-4 font-head text-lg font-bold">Proiecte la risc (marjă / întârzieri / risc)</h3>
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02] text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5 text-left font-semibold">Proiect</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Milestone</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Risc</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Marjă %</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Întârziate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {d.projects_at_risk.map((p) => (
                  <tr key={p.id} onClick={() => navigate(`/projects/${p.id}`)} className="cursor-pointer transition-colors hover:bg-white/[0.03]" data-testid={`exec-risk-${p.id}`}>
                    <td className="px-4 py-2.5"><div className="font-semibold">{p.name}</div><div className="font-mono text-xs text-muted-foreground">{p.code}</div></td>
                    <td className="px-4 py-2.5 text-muted-foreground">{p.milestone}</td>
                    <td className="px-4 py-2.5"><RiskDot level={p.risk_level} label /></td>
                    <td className={cn("px-4 py-2.5 text-right font-bold tabular-nums", p.margin_pct < 15 ? "text-red-400" : "")}>{p.margin_pct}%</td>
                    <td className="px-4 py-2.5 text-right">{p.overdue_tasks > 0 ? <span className="rounded bg-red-500/15 px-1.5 py-0.5 text-xs font-bold text-red-400">{p.overdue_tasks}</span> : "0"}</td>
                  </tr>
                ))}
                {d.projects_at_risk.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Niciun proiect la risc. 🎉</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-6" data-testid="escalation-history">
          <h3 className="mb-1 font-head text-lg font-bold">Istoric escaladări (audit)</h3>
          <p className="mb-4 text-xs text-muted-foreground">Dovadă de audit — cine/ce/când a fost escaladat (M18).</p>
          <div className="space-y-1.5">
            {history.slice(0, 30).map((h) => (
              <div key={h.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2 text-sm" data-testid={`esc-log-${h.id}`}>
                <span className={cn("shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase",
                  h.level === "escalare" ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400")}>
                  {h.level === "escalare" ? "escaladat" : "reminder"}
                </span>
                <span className="shrink-0 rounded bg-white/5 px-1.5 py-0.5 text-[10px] font-bold uppercase text-muted-foreground">{h.kind}</span>
                <span className="min-w-0 flex-1 truncate">{h.subject}</span>
                {h.target && <span className="shrink-0 text-xs text-muted-foreground">→ {h.target}</span>}
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{new Date(h.at).toLocaleString("ro-RO", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
              </div>
            ))}
            {history.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Niciun eveniment de escaladare încă.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
